import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TypePageActions } from "../Plans";
import { AddBtn } from "@/App/Employees/Add";
import { TableData } from "@/App/Employees/Table";
import { getEmployeesData } from "@/Api/employees";
import { useAuth } from "@/hooks/auth";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { Users } from "lucide-react";
import { EmployeesExtraFilters } from "@/App/Employees/Filters";
import { UnityFilter, useUnityFilter } from "@/components/UnityFilter";
import { PageHeader } from "@/components/PageHeader";

export type TableDataProps = {
  id: number;
  uuid: string;
  has_employee?: boolean;
  user_id: number;
  unity_id?: number | null;
  phone: string;
  document: string;
  is_active: number;
  created_at: string;
  admission_date: string | null;
  // Dados Pessoais
  father_name: string | null;
  mother_name: string | null;
  birth_date: string | null;
  birthplace: string | null;
  birthplace_state: string | null;
  nationality: string | null;
  gender: number | null;
  marital_status: number | null;
  ethnicity: number | null;
  education_level: number | null;
  mobile_phone: string | null;
  // Documentos
  ctps_number: string | null;
  ctps_series: string | null;
  ctps_state: string | null;
  pis_pasep: string | null;
  rg_number: string | null;
  rg_issuing_agency: string | null;
  rg_state: string | null;
  driver_license_number: string | null;
  // Dados Contratuais
  payment_frequency: number | null;
  termination_date: string | null;
  address: {
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
  };
  unity: {
    id: string;
    name: string;
  };
  sector: {
    id: string;
    name: string;
  } | null;
  position: {
    id: string;
    name: string;
  } | null;
  work_schedule: {
    id: string;
    name: string;
  } | null;
  user: {
    id: number;
    roles: {
      id: number;
      name: string;
    }[];
    uuid: string;
    name: string;
    photo: string;
    email: string;
    user_type_id: number;
    tenant_id: number;
    is_active: number;
  };
};

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const user = useAuth();
  const unityFilter = useUnityFilter();
  // Responsável de unidade sem a unidade ESCRITÓRIO na lista cai na própria unidade.
  const unityId =
    unityFilter.param ??
    (user?.user?.unity?.[0]?.id ? String(user.user.unity[0].id) : "");
  useEffect(() => {
    if (!unityFilter.ready) return;
    (async () => {
      setState("loading");
      const { data } = await getEmployeesData(1, unityId, unityFilter.nameParam, unityFilter.appliedExtra);
      setListData(data?.response);
      setState("production");
    })();
  }, [unityFilter.ready, unityId, unityFilter.nameParam, unityFilter.appliedExtra]);
  const { hasPermission } = usePermissions();

  const pageActions: TypePageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        setState("loading");

        const { data } = await getEmployeesData(
          listData?.current_page + 1,
          unityId,
          unityFilter.nameParam,
          unityFilter.appliedExtra
        );
        setListData(data.response);
        setState("production");
      }
      return;
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        setState("loading");

        const { data } = await getEmployeesData(
          listData?.current_page - 1,
          unityId,
          unityFilter.nameParam,
          unityFilter.appliedExtra
        );
        setListData(data.response);
        setState("production");
      }
      return;
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        setState("loading");

        const { data } = await getEmployeesData(listData?.last_page, unityId, unityFilter.nameParam, unityFilter.appliedExtra);
        setListData(data?.response);
        setState("production");
      }
      return;
    },
  };

  return (
    <Main>
      <If condition={hasPermission("view-employees-list")}>
        <PageHeader
          icon={Users}
          title="Colaboradores"
          description="Gerencie os colaboradores da empresa"
        >
          <If condition={hasPermission("create-employee")}>
            <AddBtn updateFront={setListData} />
          </If>
        </PageHeader>
        <div className="flex flex-col gap-3">
          <UnityFilter filter={unityFilter} namePlaceholder="Buscar por nome">
            <EmployeesExtraFilters filter={unityFilter} />
          </UnityFilter>
          {state === "loading" ? (
            <CreateLoaderTable cols={4} lines={6} />
          ) : (
            <TableData
              dataApi={listData}
              pageActions={pageActions}
              updateFront={setListData}
            />
          )}
        </div>
      </If>
    </Main>
  );
};
